import React, { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

// BulletPassThrough overlays a small animated bullet that flies left-to-right
// across the wrapped child. It pauses between passes, then loops. A muzzle
// flash appears at the entry side and a tiny impact crack at the exit.
//
// Props:
//   pierceY        : vertical position (0..1, default 0.5) — where the bullet line is
//   duration       : seconds for one pass (default 0.7)
//   interval       : seconds between passes (default 3.4)
//   thickness      : visual thickness of the bullet (default 4px)
//   trailColor     : trail tint (default gold)
//   impactCrack    : whether to render a cracked-glass overlay on impact (default true)
//   children       : the element to overlay the animation onto
//
// Usage:
//   <BulletPassThrough><h1>BLACK AXE</h1></BulletPassThrough>
//   <BulletPassThrough pierceY={0.32} duration={0.9}><img src="poster.jpg"/></BulletPassThrough>
export default function BulletPassThrough({
  children,
  pierceY = 0.5,
  duration = 0.7,
  interval = 3.4,
  thickness = 4,
  trailColor = '#FFE9A8',
  impactCrack = true,
}) {
  const bulletControls = useAnimationControls();
  const flashControls = useAnimationControls();
  const crackControls = useAnimationControls();

  const [passNo, setPassNo] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loop = async () => {
      while (!cancelled) {
        // Reset position off-screen left
        await bulletControls.set({ left: '-12%', opacity: 0 });
        await crackControls.set({ opacity: 0, scale: 0.5 });
        await flashControls.set({ opacity: 0, scale: 0.4 });

        // Wait between passes
        await new Promise((r) => setTimeout(r, interval * 1000));
        if (cancelled) return;

        // Muzzle flash at entry
        flashControls.start({
          opacity: [0, 1, 0],
          scale: [0.4, 1.4, 0.8],
          transition: { duration: 0.18, ease: 'easeOut' },
        });

        // Bullet streak across
        await bulletControls.start({
          left: '105%',
          opacity: [0, 1, 1, 1, 0],
          transition: { duration, ease: [0.2, 0, 0.4, 1], times: [0, 0.05, 0.45, 0.85, 1] },
        });

        // Impact crack at exit
        if (impactCrack) {
          crackControls.start({
            opacity: [0, 0.95, 0.55, 0],
            scale: [0.6, 1.0, 1.05, 1.1],
            transition: { duration: 1.4, ease: 'easeOut' },
          });
        }

        setPassNo((n) => n + 1);
      }
    };
    loop();
    return () => {
      cancelled = true;
    };
  }, [bulletControls, flashControls, crackControls, duration, interval, impactCrack]);

  const yTop = `calc(${pierceY * 100}% - ${thickness / 2}px)`;

  return (
    <span className="relative inline-block w-full" style={{ display: 'inline-block' }}>
      {children}

      {/* Muzzle flash on the left side */}
      <motion.span
        aria-hidden="true"
        animate={flashControls}
        initial={{ opacity: 0, scale: 0.4 }}
        className="pointer-events-none absolute"
        style={{
          left: '-3.5%',
          top: yTop,
          width: 60,
          height: 60,
          transform: 'translateY(-50%)',
          background:
            'radial-gradient(circle at 50% 50%, rgba(255,233,168,0.95) 0%, rgba(255,138,58,0.6) 35%, rgba(0,0,0,0) 70%)',
          filter: 'blur(2px)',
        }}
      />

      {/* The bullet streak itself — a horizontal capsule with motion trail */}
      <motion.span
        aria-hidden="true"
        animate={bulletControls}
        initial={{ left: '-12%', opacity: 0 }}
        className="pointer-events-none absolute"
        style={{
          top: yTop,
          height: thickness,
          width: 80,
          borderRadius: thickness,
          background: `linear-gradient(90deg, rgba(0,0,0,0) 0%, ${trailColor} 35%, #FFFFFF 75%, #FFE9A8 100%)`,
          boxShadow: `0 0 14px ${trailColor}, 0 0 32px rgba(255,170,80,0.65)`,
        }}
      />

      {/* Cracked-glass / impact ring at exit (right side) */}
      {impactCrack && (
        <motion.span
          aria-hidden="true"
          animate={crackControls}
          initial={{ opacity: 0, scale: 0.5 }}
          className="pointer-events-none absolute"
          style={{
            right: '-2%',
            top: yTop,
            width: 70,
            height: 70,
            transform: 'translateY(-50%)',
            background:
              'radial-gradient(circle at 50% 50%, rgba(255,234,168,0.85) 0%, rgba(216,40,28,0.45) 28%, rgba(0,0,0,0) 70%)',
            filter: 'blur(0.6px)',
            mixBlendMode: 'screen',
          }}
        />
      )}
    </span>
  );
}
